import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionCard } from "@/components/shared/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Admin-only explicit pre-link: verified Microsoft tenant id + object id (oid) -> an existing staff user. No e-mail matching.
export function IdentityLinksPanel({ staffUsers }) {
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState({ user_id: "", tenant_id: "", object_id: "" });
  const load = () => api.get("/admin/identity-links").then((r) => setLinks(r.data.items)).catch(() => setLinks([]));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/identity-links", form); toast.success("Identity linked (audited)"); setForm({ user_id: "", tenant_id: "", object_id: "" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not link"); }
  };
  const revoke = async (id) => { try { await api.delete(`/admin/identity-links/${id}`); toast.success("Link revoked (audited)"); load(); } catch { toast.error("Could not revoke"); } };
  return (
    <SectionCard title="Microsoft work-account links" subtitle="Sign-in is granted only to staff pre-linked here by tenant id + object id (oid) from Entra. Nothing is inferred from e-mail." testId="identity-links" bodyClassName="px-5 pb-5">
      <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <select data-testid="identity-link-user" className="h-9 rounded-md border border-jigsaw-border bg-white px-2 text-sm" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required>
          <option value="">Staff user…</option>
          {(staffUsers || []).map((u) => <option key={u.id} value={u.id} label={`${u.full_name} (${u.email})`} />)}
        </select>
        <Input data-testid="identity-link-tenant" placeholder="Entra tenant id (GUID)" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} required />
        <Input data-testid="identity-link-oid" placeholder="User object id (oid GUID)" value={form.object_id} onChange={(e) => setForm({ ...form, object_id: e.target.value })} required />
        <Button data-testid="identity-link-submit" type="submit" size="sm">Link</Button>
      </form>
      <ul className="mt-3 divide-y divide-jigsaw-border text-[13px]">
        {links === null ? <li className="py-2 text-jigsaw-muted">Loading…</li> : links.length === 0 ? <li data-testid="identity-links-empty" className="py-2 text-jigsaw-muted">No staff identities linked yet - Microsoft sign-in cannot admit anyone until an Admin links a verified oid.</li>
          : links.map((l) => (
            <li key={l.id} data-testid={`identity-link-${l.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span><span className="font-medium text-jigsaw-navy">{l.full_name}</span> <span className="text-jigsaw-muted">{l.email}</span> <span className="font-mono-ref text-[11px] text-jigsaw-muted">oid …{l.object_id.slice(-6)}</span></span>
              <Button data-testid={`identity-link-revoke-${l.id}`} size="sm" variant="ghost" className="text-jigsaw-danger" onClick={() => revoke(l.id)}>Revoke</Button>
            </li>
          ))}
      </ul>
    </SectionCard>
  );
}
